import { useEffect, useMemo, useState } from "react";
import type {
  BusinessPartner,
  SerieItem,
  TipoCE,
} from "../types/modernInvoiceForm";
import { series_get } from "../../../../services/invoices/invoices";
import { pagination_method_ts } from "../../../../main_components/pagination/pagination_ts";

export function useClientSection(params: {
  cfdirelacionados?: Array<{ uuid: string }>;
  setCfdirelacionados?: (v: any[]) => void;
}) {
  const { cfdirelacionados = [], setCfdirelacionados } = params;

  // ---------- Tipo (I/E) ----------
  const [tipo, setTipo] = useState<TipoCE>("I");

  // ---------- Series ----------
  const [seriesCache, setSeriesCache] = useState<
    Partial<Record<TipoCE, SerieItem[]>>
  >({});
  const [seriesOptions, setSeriesOptions] = useState<SerieItem[]>([]);
  const [selectedSerie, setSelectedSerie] = useState<SerieItem | null>(null);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesError, setSeriesError] = useState<string | null>(null);

  const folio = useMemo(
    () => (selectedSerie ? String(selectedSerie.folio) : ""),
    [selectedSerie]
  );

  const applySeries = (list: SerieItem[]) => {
    setSeriesOptions(list || []);
    setSelectedSerie((prev) => {
      if (prev && list.some((s) => s.serie === prev.serie)) return prev;
      return list?.[0] ?? null;
    });
  };

  const fetchSeries = async (type: TipoCE, opts?: { force?: boolean }) => {
    const force = !!opts?.force;
    if (!force && seriesCache[type]?.length) {
      applySeries(seriesCache[type]!);
      return;
    }
    if (seriesLoading) return;

    setSeriesLoading(true);
    setSeriesError(null);
    try {
      const res: SerieItem[] = (await series_get(type)).data;
      setSeriesCache((prev) => ({ ...prev, [type]: res }));
      applySeries(res);
    } catch (err: any) {
      console.log("error series", err);
      setSeriesError("No se pudieron cargar las series.");
    } finally {
      setSeriesLoading(false);
    }
  };

  const handleChangeTipo = (nuevo: string) => {
    const nt = (nuevo === "E" ? "E" : "I") as TipoCE;
    setTipo(nt);
    setSelectedSerie(null);
    setSeriesOptions([]);
    fetchSeries(nt);
  };

  const handleForceRefreshSeries = () => {
    setSelectedSerie(null);
    setSeriesOptions([]);
    fetchSeries(tipo, { force: true });
  };

  const setSerieByValue = (serieValue: string) => {
    const found = seriesOptions.find((x) => x.serie === serieValue) || null;
    setSelectedSerie(found);
  };

  // carga inicial
  useEffect(() => {
    fetchSeries("I");
  }, []);

  // ---------- Clientes ----------
  const [clientesList, setClientesList] = useState<BusinessPartner[]>([]);
  const [selectedCliente, setSelectedCliente] =
    useState<BusinessPartner | null>(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);

  const fetchBusinessPartners = async (_opts?: { force?: boolean }) => {
    if (clientsLoading) return;

    setClientsLoading(true);
    setClientsError(null);
    try {
      const res = (await pagination_method_ts("bp", null)) as BusinessPartner[];
      setClientesList(res || []);
      setSelectedCliente((prev) => {
        if (prev && res.some((c) => c.id === prev.id)) return prev;
        return null;
      });
    } catch (err: any) {
      console.log("error socios", err);
      setClientsError("No se pudieron cargar los socios.");
    } finally {
      setClientsLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessPartners();
  }, []);

  // Confirmación al cambiar si hay relacionados
  const handleSelectClienteGuardado = (nuevo: BusinessPartner | null) => {
    const hayRelacionados = (cfdirelacionados?.length || 0) > 0;
    const estaCambiando = (nuevo?.id || null) !== (selectedCliente?.id || null);

    if (hayRelacionados && estaCambiando) {
      const ok = window.confirm(
        "Este comprobante tiene CFDI relacionados.\n" +
          "Si cambias o borras el socio de negocios, se eliminarán las relaciones vinculadas.\n\n" +
          "¿Deseas continuar?"
      );
      if (!ok) return;
      setCfdirelacionados?.([]); // limpiar relaciones
    }
    setSelectedCliente(nuevo);
  };

  // ---- NUEVO: limpiar cliente sin confirmación (para resets)
  const clearCliente = () => {
    setSelectedCliente(null);
    // también limpiamos los datos fiscales reflejados en el formulario
    setRfc("");
    setUsoCFDIValue("");
    setRegimenFiscal("");
  };

  const handleForceRefreshClients = () => {
    clearCliente();
    setClientesList([]);
    fetchBusinessPartners({ force: true });
  };

  // ---------- Datos fiscales mostrados en Client ----------
  const [rfc, setRfc] = useState<string>("");
  const [usoCFDIValue, setUsoCFDIValue] = useState<string>("");
  const [regimenFiscal, setRegimenFiscal] = useState<string>("");

  useEffect(() => {
    if (!selectedCliente) {
      setRfc("");
      setUsoCFDIValue("");
      setRegimenFiscal("");
      return;
    }
    setRfc(selectedCliente.rfc || "");
    setUsoCFDIValue(selectedCliente.cfdi_use || "");
    setRegimenFiscal(selectedCliente.tax_regime || "");
  }, [selectedCliente]);

  // ---------- Menú QR ----------
  const [qrAnchorEl, setQrAnchorEl] = useState<HTMLElement | null>(null);
  const qrMenuOpen = Boolean(qrAnchorEl);
  const handleOpenQRMenu = (anchor: HTMLElement | null) =>
    setQrAnchorEl(anchor);
  const handleCloseQRMenu = () => setQrAnchorEl(null);

  const selectClienteByIdOrObject = (bp: { id: number | null } | null) => {
    if (!bp || !bp.id) {
      setSelectedCliente(null);
      return;
    }
    const found =
      clientesList.find((c) => Number(c.id) === Number(bp.id)) || (bp as any);
    handleSelectClienteGuardado(found as any);
  };

  return {
    // tipo/series
    tipo,
    handleChangeTipo,
    seriesOptions,
    selectedSerie,
    setSerieByValue,
    folio,
    handleForceRefreshSeries,
    seriesLoading,
    seriesError,

    // clientes
    clientesList,
    selectedCliente,
    onSelectCliente: handleSelectClienteGuardado,
    handleForceRefreshClients,
    clearCliente, // <-- exportamos el limpiador
    clientsLoading,
    clientsError,

    // datos fiscales
    rfc,
    usoCFDIValue,
    setUsoCFDIValue,
    regimenFiscal,
    setRegimenFiscal,

    // QR menu
    qrAnchorEl,
    qrMenuOpen,
    handleOpenQRMenu,
    handleCloseQRMenu,
    selectClienteByIdOrObject,
  };
}
